import { useEffect, useMemo, useState } from "react";

import type { GeneratedMeetingQuestion } from "../types/meetings";

import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Textarea } from "./ui";

export interface ReviewedMeetingQuestion {
  question: string;
  category?: string | null;
  intent?: string | null;
}

interface ReviewGeneratedMeetingQuestionsModalProps {
  isOpen: boolean;
  meetingTitle?: string;
  questions: GeneratedMeetingQuestion[];
  isSubmitting?: boolean;
  onCancel: () => void;
  onConfirm: (questions: ReviewedMeetingQuestion[]) => void;
}

interface QuestionDraft extends ReviewedMeetingQuestion {
  id: string;
  removed?: boolean;
}

export function ReviewGeneratedMeetingQuestionsModal({
  isOpen,
  meetingTitle,
  questions,
  isSubmitting,
  onCancel,
  onConfirm,
}: ReviewGeneratedMeetingQuestionsModalProps): JSX.Element | null {
  const [drafts, setDrafts] = useState<QuestionDraft[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    setDrafts(
      questions.map((item, index) => ({
        id: `${index}-${item.question.slice(0, 6)}`,
        question: item.question,
        category: item.category ?? undefined,
        intent: item.intent ?? undefined,
      })),
    );
  }, [isOpen, questions]);

  const hasQuestions = useMemo(() => drafts.some((item) => !item.removed), [drafts]);

  const handleChange = (id: string, changes: Partial<QuestionDraft>) => {
    setDrafts((current) => current.map((draft) => (draft.id === id ? { ...draft, ...changes } : draft)));
  };

  const handleRemove = (id: string) => {
    setDrafts((current) => current.map((draft) => (draft.id === id ? { ...draft, removed: true } : draft)));
  };

  const handleRestore = (id: string) => {
    setDrafts((current) => current.map((draft) => (draft.id === id ? { ...draft, removed: false } : draft)));
  };

  const handleAdd = () => {
    setDrafts((current) => [
      ...current,
      {
        id: `custom-${current.length + 1}`,
        question: "",
        category: "",
        intent: "",
      },
    ]);
  };

  const handleConfirm = () => {
    const normalized = drafts
      .filter((item) => !item.removed && item.question.trim())
      .map((item) => ({
        question: item.question.trim(),
        category: item.category?.trim() || null,
        intent: item.intent?.trim() || null,
      }));

    onConfirm(normalized);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onCancel() : null)}>
      <DialogContent className="max-h-[90vh] w-full max-w-4xl overflow-y-auto p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-4">
          <div className="text-sm font-semibold uppercase text-slate-500">AI suggestions</div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-xl font-bold text-slate-900">Review generated questions</DialogTitle>
              {meetingTitle ? (
                <div className="text-sm text-slate-600">Prepared for: {meetingTitle}</div>
              ) : null}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
              AI-assisted draft
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-4 p-6">
          <p className="text-sm text-slate-700">
            Keep the questions that fit your session, adjust their wording, and add categories to help
            participants understand the intent. Removed questions will be discarded when the meeting is
            created.
          </p>

          {hasQuestions ? (
            <div className="flex flex-col gap-3">
              {drafts.map((item, index) => (
                <div
                  key={item.id}
                  className={`rounded border ${item.removed ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}
                >
                  <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span className="rounded bg-slate-100 px-2 py-1">{index + 1}</span>
                      {item.category?.trim() ? item.category : "Uncategorized"}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      {item.removed ? (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="text-emerald-700"
                          onClick={() => handleRestore(item.id)}
                        >
                          Restore
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="text-rose-600"
                          onClick={() => handleRemove(item.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-3 p-4 md:grid-cols-[1fr_1fr]">
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      <span className="text-xs font-semibold uppercase text-slate-500">Category</span>
                      <Input
                        type="text"
                        value={item.category ?? ""}
                        onChange={(event) => handleChange(item.id, { category: event.target.value })}
                        placeholder="Discovery, Risks, Timeline..."
                      />
                    </label>

                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      <span className="text-xs font-semibold uppercase text-slate-500">Intent</span>
                      <Input
                        type="text"
                        value={item.intent ?? ""}
                        onChange={(event) => handleChange(item.id, { intent: event.target.value })}
                        placeholder="What outcome are you targeting?"
                      />
                    </label>

                    <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
                      <span className="text-xs font-semibold uppercase text-slate-500">Question</span>
                      <Textarea
                        className="min-h-[72px]"
                        value={item.question}
                        onChange={(event) => handleChange(item.id, { question: event.target.value })}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded border border-dashed border-slate-300 p-6 text-center text-sm text-slate-600">
              No questions to review. Add your own prompts below.
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button type="button" variant="secondary" onClick={handleAdd}>
              Add question
            </Button>
            <div className="text-xs text-slate-500">
              Questions removed here will not be included when the meeting is created.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? "Creating meeting..." : "Use selected questions"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
